import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshBilling } = useAuth();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const me = await api.get("/billing/me");
          refreshBilling(me.data);
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries.current >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="surface p-8 max-w-md w-full text-center space-y-4 fade-up" data-testid="payment-success-page">
        {state === "checking" && <><Loader2 className="h-10 w-10 mx-auto animate-spin text-emerald-600" /><h1 className="font-heading text-2xl font-bold">Confirming your subscription...</h1><p className="text-sm text-muted-foreground">This only takes a moment.</p></>}
        {state === "paid" && <><CheckCircle className="h-12 w-12 mx-auto text-emerald-600" /><h1 className="font-heading text-2xl font-bold" data-testid="payment-success-title">You're in!</h1><p className="text-sm text-muted-foreground">Your 14-day free trial has started. Nothing is charged until it ends — cancel anytime from Settings.</p>
          <Button data-testid="payment-success-continue" onClick={() => navigate("/clients")} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">Go to my clients</Button></>}
        {(state === "timeout" || state === "error") && <><AlertTriangle className="h-12 w-12 mx-auto text-amber-500" /><h1 className="font-heading text-2xl font-bold">Still confirming</h1><p className="text-sm text-muted-foreground">We haven't received confirmation yet. If you completed checkout, it will activate shortly.</p>
          <Button data-testid="payment-success-billing" variant="outline" onClick={() => navigate("/billing")} className="w-full">Back to billing</Button></>}
      </div>
    </div>
  );
}
